/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  TemplateRef,
  ChangeDetectionStrategy,
  Inject,
  AfterViewInit,
  PLATFORM_ID,
  HostBinding,
  computed,
  effect,
  forwardRef,
  inject,
  OnDestroy,
  signal
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { faBars, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { AutoComplete } from 'primeng/autocomplete';
import { Menu } from 'primeng/menu';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { ENVIRONMENT, Environment, EnvironmentService } from '@environment';
import { AuthService } from '@usheru-hff/authentication/domain';
import { MyActivityService } from '@usheru-hff/community-elements/utils';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { HeaderService } from '@usheru-hff/sdk/header';
import { NavigationService } from '@usheru-hff/sdk/navigation';
import { TRACKING_SERVICE, TrackingService } from '@usheru-hff/sdk/tracking';
import { toBoolean } from '@usheru-hff/sdk/utils/utils';
import { Channel, Movie, PageLink, SocialNetworkLink, UserRole } from '@usheru-hff/shared/domain';
import { HEADER_INITIAL_LAYOUT_MODE, type HeaderLayoutMode } from '@usheru-hff/shared/tokens';
import { PopupCustomPageService, TemplatesService, BrandedPageThemeService } from '@usheru-hff/shared/utils';
import { CollectionRoutesEnum as Routes } from '@usheru-hff/shared/utils-community';
import { bindHostSignalAttrs } from '@usheru-hff/ui/utils';

import { HEADER_ANIMATION } from './header.animation';
import { HeaderDebugDirective } from './header.debug.directive';
import { HEADER_DEBUG_HOST } from './header.debug.host';
import { HeaderCustomization } from 'libs/environment/src/lib/environment/environment-props';

const MENU_TOGGLER_BRANDED = $localize`:@@public.header.menu-toggler-branded:_channel_`;
const DEFAULT_HEADER_DESKTOP_MIN_WIDTH = 992;
const HEADER_DESKTOP_MIN_WIDTH_CSS_VAR = '--header-desktop-min-width';
@Component({
  selector: 'ush-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: HEADER_ANIMATION,
  standalone: false,
  providers: [
    {
      provide: HEADER_DEBUG_HOST,
      useExisting: forwardRef(() => HeaderComponent)
    }
  ],
  hostDirectives: [HeaderDebugDirective],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly documentRef = inject(DOCUMENT);
  private readonly envService = inject(EnvironmentService);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly initialHeaderLayoutMode = inject(HEADER_INITIAL_LAYOUT_MODE, { optional: true });

  readonly dynamicHostAttrsEnabled = resolveDynamicHostAttrsEnabled(this.envService.headerConfig());
  readonly trackScrollEnabled = this.envService.headerConfig()?.onTopTracking;
  private readonly hostBindingEnabledState = signal(this.dynamicHostAttrsEnabled);
  private readonly trackScrollEnabledState = signal(this.trackScrollEnabled);

  isBrowser = isPlatformBrowser(this.platform);

  @HostBinding('style.display') displayStyle: 'block' | 'none';

  /**
   * Used to know if the header has already entered the scrolled state at least once.
   */
  readonly hasScrolled = signal<boolean | undefined>(false);

  readonly isFixed = signal<boolean | undefined>(undefined);

  readonly scrolledStatus = signal<'top' | 'scrolled' | 'initial' | null | undefined>(null);
  /**
   * Used for the transition from `fixed` to `absolute`.
   */
  readonly scrollDirection = signal<'up' | 'down' | null | undefined>(null);

  /**
   * Check the `application.global.scss` to find the styling related to these attributes.
   */
  readonly overContentLg = signal<boolean | undefined>(false);

  readonly overContentSm = signal<boolean | undefined>(false);

  /**
   * Used to know if at TOP has the over content enabled. The {@link overContentLg} can be
   * altered when scrolling and will help to set some styles accordingly.
   */
  readonly isOverContentAtTopLg = signal<boolean | undefined>(false);

  /**
   * Used to know if at TOP has the over content enabled. The {@link overContentSm} can be
   * altered when scrolling and will help to set some styles accordingly.
   */
  readonly isOverContentAtTopSm = signal<boolean | undefined>(false);

  readonly overContentScrolledLg = computed(() => {
    if (!this.dynamicHostAttrsEnabled) return undefined;
    const config = this.envService.headerConfig();
    const scrolledOverContentLg = config?.scrolledCustomization?.positionOverContentLg;
    return config?.onTopTracking && scrolledOverContentLg !== undefined && scrolledOverContentLg !== null
      ? scrolledOverContentLg
      : this.overContentLg();
  });

  readonly overContentScrolledSm = computed(() => {
    if (!this.dynamicHostAttrsEnabled) return undefined;
    const config = this.envService.headerConfig();
    const scrolledOverContentSm = config?.scrolledCustomization?.positionOverContentSm;
    return config?.onTopTracking && scrolledOverContentSm !== undefined && scrolledOverContentSm !== null
      ? scrolledOverContentSm
      : this.overContentSm();
  });

  readonly logoFilter = signal<HeaderCustomization['logoFilter'] | undefined>(
    this.envService.headerConfig()?.logoFilter || 'none'
  );

  /**
   * Used to enable some animation to transition between normal and scrolled header if has different styles.
   */
  readonly hasDifferentScrolledClass = signal<boolean | undefined>(false);
  /**
   * Drives the two-phase mobile return animation for overlay headers:
   * slide the scrolled header out first, then slide the top header back in.
   */
  readonly mobileOverlayTopTransitionPhase: ReturnType<typeof signal<'out' | 'in' | null>> = signal(null);
  readonly headerLayoutMode = signal<HeaderLayoutMode | undefined>(this.getInitialHeaderLayoutMode());

  private readonly hostSignalAttrBindings = bindHostSignalAttrs({
    host: this.hostElement,
    bindings: [
      { attr: 'host-binding-enabled', value: this.hostBindingEnabledState },
      { attr: 'data-track-scroll-enabled', value: this.trackScrollEnabledState },
      { attr: 'data-position-fixed', value: this.isFixed },
      { attr: 'data-scroll-position', value: this.scrolledStatus },
      { attr: 'data-scroll-direction', value: this.scrollDirection },
      { attr: 'data-over-content-lg', value: this.overContentLg },
      { attr: 'data-over-content-sm', value: this.overContentSm },
      { attr: 'data-over-content-at-top-lg', value: this.isOverContentAtTopLg },
      { attr: 'data-over-content-at-top-sm', value: this.isOverContentAtTopSm },
      { attr: 'data-over-content-scrolled-lg', value: this.overContentScrolledLg },
      { attr: 'data-over-content-scrolled-sm', value: this.overContentScrolledSm },
      { attr: 'data-logo-filter', value: this.logoFilter },
      { attr: 'data-has-different-scrolled-class', value: this.hasDifferentScrolledClass },
      { attr: 'data-has-scrolled', value: this.hasScrolled },
      { attr: 'data-mobile-overlay-top-transition', value: this.mobileOverlayTopTransitionPhase },
      { attr: 'data-header-layout', value: this.headerLayoutMode }
    ]
  });

  /**
   * List of {@link AutoComplete} inside the {@link SearchMovieComponent}. Should be one
   * in the normal header and other in the small header.
   */
  @ViewChildren('autocomplete_movie', { read: AutoComplete })
  autocomplete: QueryList<AutoComplete>;

  @ViewChild('headerThreshold', { static: true }) headerThreshold: ElementRef;

  /**
   * @deprecated Don't found any use
   */
  @ViewChild('searchItem')
  searchItemRef: ElementRef;

  /**
   * @deprecated Don't found any use
   */
  @ViewChild('menu')
  menu: Menu;

  headerCustomizationClass = signal<string>('');
  private thresholdScrollHandler?: () => void;
  private resizeHandler?: () => void;
  private isScrollTrackingReady = false;
  private isScrollTrackingEnabled = false;
  private mobileOverlayTopTransitionTimers: ReturnType<typeof setTimeout>[] = [];

  private get browserWindow(): Window | null {
    return this.isBrowser ? this.documentRef.defaultView : null;
  }

  /**
   * Indicates if is seletected the normal o smal header.
   */
  isCollapse = false;

  /**
   * Indicates if the translation widget should be in the collapsed menu (mobile).
   */
  hasCollapsedTranslation = false;

  /**
   * FontAwsome icon
   */
  toggleIcon = faBars;

  /**
   * @deprecated Don't found any use
   */
  spanCollapse = false;

  /**
   * @deprecated Don't found any use
   */
  spanFullTransparency = true;

  /**
   * Logo image
   */
  logoUrl: string;

  /**
   * Reddirection of the logo. Can be internal or external. No options to be null.
   */
  logoLink: string;

  /**
   * Fragment attache to the {@link logoLink}.
   */
  logoFragment: string;

  /**
   * Params to render the {@link logoUrl}
   */
  logoParams: { width?: number; height?: number };

  /**
   * Indicate if the {@link logoLink} is a internal or external link.
   */
  logoLinkExternal = false;

  /**
   * @deprecated Don't found any use
   */
  isSearch = false;

  /**
   * @deprecated Don't found any use
   */
  isStyleClip = false;

  /**
   * @deprecated Don't found any use
   */
  searchIcon = faSearch;

  /**
   * @deprecated Don't found any use
   */
  movie: Movie;

  /**
   * List of movie to show in the {@link SearchMovieComponent}.
   */
  movieSuggestions: Movie[];

  /**
   * Indicates if is in a branded movie menu for small devices.
   */
  onMovieMenu = true;

  /**
   * Links to show in the menu.
   */
  links: PageLink[] = [];

  /**
   * Indicate if the must show the social icons. This is set from enviroment settings.
   */
  hasSocialLinks = false;

  /**
   * List of social links to display.
   */
  socialLinks: SocialNetworkLink[] = [];

  /**
   * @deprecated Don't found any use
   */
  homePage: string;

  /**
   * @docsNotRequired
   */
  faTimes = faTimes;

  /**
   * Movie to get some properties when branded movie menu.
   */
  brandedMovie: Movie | null;

  /**
   * If it is in branded page get the links for the custom menu for this pages.
   */
  brandedPageLinks: PageLink[] = [];

  /**
   * If it is in branded page get the styles for the custom menu for this pages.
   */
  _brandedPageMenuStyle: string;

  brandStyleInBody: HTMLElement;

  get brandedPageMenuStyle(): string {
    return this._brandedPageMenuStyle;
  }

  set brandedPageMenuStyle(x: string | null) {
    if (!this.isBrowser) return;
    if (x) {
      this._brandedPageMenuStyle = `<style type="text/css">${x.replace(/\r?\n|\r|(\s\s\s*)/g, '')}</style>`;
      this.brandStyleInBody = this.documentRef.createElement('style');
      this.brandStyleInBody.innerHTML = x;
      const body = this.documentRef.getElementsByClassName('route-branded-movie')[0];
      if (body) body.appendChild(this.brandStyleInBody);
    } else {
      this._brandedPageMenuStyle = x;
    }
  }

  /**
   *  Used for redirection after login/register process.
   */
  returnUrl: string;

  //
  /**
   * Custom template provided by {@link TemplatesService} to add custom entries in the header menu.
   */
  headerCustomLinkTemplate: TemplateRef<unknown>;

  /**
   * Reference to "this" component to use in the {@link headerCustomLinkTemplate}.
   */
  headerInstance: unknown;

  readonly elementLocation = this.envConfig<Record<string, string>>('elementLocation');
  readonly elementSearchLocation = this.elementLocation?.search || '1';
  readonly elementLogoLocation = this.elementLocation?.logo || '2';
  readonly elementLoginLocation = this.elementLocation?.login || '1';

  /**
   * @docsNotRequired
   */
  channel: Channel;

  destroy$ = new Subject<boolean>();

  constructor(
    private location: Location,
    private authenticationService: AuthService,
    private brandedPageThemeService: BrandedPageThemeService,
    @Inject(ENVIRONMENT) private env: Environment,
    @Inject(PLATFORM_ID) private platform,
    private router: Router,
    private navigationService: NavigationService,
    private channelService: ChannelService,
    private ref: ChangeDetectorRef,
    private popupCustomPage: PopupCustomPageService,
    private templatesService: TemplatesService,
    private cd: ChangeDetectorRef,
    @Inject(TRACKING_SERVICE) private trackingService: TrackingService,
    private myActivityService: MyActivityService,
    private headerService: HeaderService
  ) {
    effect(() => {
      this.displayStyle = this.headerService.displayStyle();
      this.syncHostRuntimeBindings();
      this.cd.markForCheck();
    });

    //
    this.channel = this.channelService.channel;

    const logoConfig = this.env?.logosConfig?.header;
    this.hasCollapsedTranslation = this.env.customPageParams?.headerSMtranslationCollapsed;
    this.logoLink = logoConfig?.logoLink || '/home';
    this.logoFragment = logoConfig?.logoFragment;
    this.logoLinkExternal = this.logoLink.indexOf('http') == 0;
    this.logoUrl = logoConfig?.logoUrl || this.channel.logoUrl;
    const { width, height } = logoConfig || {};
    this.logoParams = { width, height };

    this.homePage = this.channel.homePage.replace('/page', '');
    this.hasSocialLinks = this.envConfig<boolean>('showSocialLinks') || this.env?.customPageParams?.headerSocialLinks;
    if (this.hasSocialLinks) {
      this.socialLinks = this.channel.socialNetworks.filter(sn => sn.showLink);
    }

    this.setInitialNotEnabledDynamicHostAttrs();

    // Warning when header component config exists but runtime host-state attrs are not enabled.
    if (!this.dynamicHostAttrsEnabled && this.env.components?.header) {
      console.warn(
        'Header runtime host-state attrs are disabled. Set components.header.dynamicHostAttrsEnabled=true to apply scroll/over-content host-state styles.'
      );
    }
  }

  /**
   * Some apps can have style conflicts when runtime host-state attrs are enabled.
   */
  setInitialNotEnabledDynamicHostAttrs() {
    if (this.dynamicHostAttrsEnabled) return;
    this.hasScrolled.set(undefined);
    this.isFixed.set(undefined);
    this.scrolledStatus.set(undefined);
    this.scrollDirection.set(undefined);
    this.overContentLg.set(undefined);
    this.overContentSm.set(undefined);
    this.logoFilter.set(undefined);
    this.hasDifferentScrolledClass.set(undefined);
    this.syncHostRuntimeBindings();
  }

  /**
   * * Sets the menu {@link links}.
   *
   * * Subscribe to {@link userSubscription}.
   *
   * * Make a subscription to {@link router} and update returnUrl of login widget accordlyng and reset active header link
   * in case it doesn't match the url.
   *
   * * Load header custom template if any.
   */
  ngOnInit(): void {
    this.headerInstance = this;
    this.links = JSON.parse(JSON.stringify(this.navigationService.getHeaderLinks()));

    this.links = this.links
      .filter(link => link.visibility == 3)
      .sort((a, b) => a.order - b.order)
      .map(link => {
        if (link.link.indexOf('http') == 0) {
          link.isExternal == true;
          return link;
        }
        const params = link.link.split('?')[1]?.split('&') || [];
        const paramsToSave = [];
        params.forEach(p => {
          const split = p.split('=');
          const name = split[0];
          const value = split[1] || '';
          switch (true) {
            case name == 'parent' && value.length > 0:
              link.parent = value;
              break;
            case name == 'popup':
              link.isPopup = true;
              break;
            default:
              paramsToSave.push(p);
          }
        });
        if (paramsToSave.length > 0) {
          link.params = paramsToSave.reduce((pre, cur) => {
            const split = cur.split('=');
            pre[split[0]] = split[1];
            return pre;
          }, {});
        }

        link.link = link.link?.split('?')[0];

        return link;
      });

    // Prepare submenus
    this.links.forEach((link, i) => {
      if (link?.parent) {
        const parent = this.links.findIndex(l => l?.name === link?.parent);
        // if parent link doesn't exit replace this as the parent.
        // And add the link as a child
        if (parent === -1) {
          const child = { ...link };
          link.name = link.parent;
          link.link = null;
          link.isParent = true;
          link.parent = null;
          link.children = [child];
          this.links[i] == link;
        } else {
          // Adds link to his parent and return null to delete later
          const childrens = this.links[parent]?.children || [];
          childrens.push(link);
          this.links[parent].children = childrens;
          if (link.isActive) this.links[parent].isActive = true;
          this.links[i] = null;
        }
      }
    });

    // Remove nulls
    this.links = this.links.filter(n => n);

    // addying community header link group if community role is present
    const communityRole = this.channelService.getCommunityRoleAvailable();
    if (communityRole) this.links.push(this.getMyActivityLinkGroup());

    this.setInitialHeaderCustomizationParams();

    this.returnUrl = this.router.url;
    //
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        distinctUntilChanged()
      )
      .subscribe((route: NavigationEnd) => {
        // resetting header customization params.
        this.setInitialHeaderCustomizationParams();
        this.isScrollTrackingReady = true;
        this.syncTrackedHeaderStateFromScroll(true);

        let linksActive = 0;
        const urlAfterRedirects = route.urlAfterRedirects.split('?')[0];
        this.links = this.links.map(link => {
          if (link?.isParent && link?.children) {
            link.children = link.children.map(l => {
              l.isActive = l.link.split('?')[0] == urlAfterRedirects;
              return l;
            });
          } else {
            link.isActive = link?.link?.split('?')[0] == urlAfterRedirects;
            // making sure there is only 1 link active
            if (link.isActive) linksActive++;
            if (linksActive > 1) link.isActive = false;
          }
          return link;
        });

        this.returnUrl = this.router.url;

        this.cd.detectChanges();
      });

    // subscribing to isBrandedPage
    this.brandedPageThemeService
      .getBrandedMovie()
      .pipe(takeUntil(this.destroy$))
      .subscribe(movie => {
        this.brandedMovie = movie;
      });

    this.brandedPageThemeService
      .getBrandedPageLinks()
      .pipe(takeUntil(this.destroy$))
      .subscribe(links => {
        this.brandedPageLinks = links;
        this.cd.detectChanges();
      });
    this.brandedPageThemeService
      .getBrandedMobileMenuStyle()
      .pipe(takeUntil(this.destroy$))
      .subscribe(style => {
        this.brandedPageMenuStyle = style;
      });

    this.loadHeaderCustomTemplate();
  }

  ngAfterViewInit(): void {
    this.setupResizeTracking();
    this.setupScrollTracking();
    this.listenForUserChanges();
  }

  brandedLabel(movieTitle: string): string {
    return MENU_TOGGLER_BRANDED.replace('_channel_', movieTitle);
  }

  isExternalLink(link: string): boolean {
    return link.indexOf('http') == 0;
  }

  /**
   *
   * Gets from enviroment if the header should be fixed.
   * @returns
   */
  private hasHeaderFixed(config: HeaderCustomization): boolean {
    return this.envConfig<boolean>('positionFixed', config) || this.env?.customPageParams?.headerFixed || false;
  }

  /**
   * Change the status of the small header to show the menu items.
   * @param value
   */
  toggleNavbar(value?: boolean) {
    this.isCollapse = value != null ? value : !this.isCollapse;
    this.toggleIcon = this.isCollapse ? faTimes : faBars;
    const documentElement = this.documentRef?.documentElement;

    if (!this.isCollapse) {
      this.spanFullTransparency = true;
      setTimeout(() => (this.spanCollapse = false), 300);
    } else {
      this.spanCollapse = true;
      setTimeout(() => (this.spanFullTransparency = false), 100);
    }
    if (documentElement) {
      if (this.isCollapse) {
        documentElement.style.setProperty('--body-overflow-y', 'hidden');
      } else {
        documentElement.style.setProperty('--body-overflow-y', 'auto');
      }
    }

    this.cd.detectChanges();
  }

  /**
   * Lauches intructions to the {@link PopupCustomPageService} to show this direction as popup.
   * @param link
   * @returns
   */
  onLinkClick(link: PageLink) {
    if (link?.isPopup) {
      const component = link.link;
      if (component.indexOf('/') === 0) component.slice(1);
      this.popupCustomPage.pushTrigger(component);
      return;
    }

    if (link?.isParent && link?.children?.length) {
      this.activateParentLink(link);
      return;
    }

    if (!link?.isExternal) {
      this.toggleNavbar(false);
    }
  }

  /**
   * @deprecated Don't found any use
   */
  toggleSearchBar() {
    this.isStyleClip = true;
    setTimeout(() => {
      this.isSearch = !this.isSearch;
      this.searchIcon = this.isSearch ? faTimes : faSearch;
      this.ref.detectChanges();

      if (this.isSearch) {
        this.getMovieSearchAutocomplete();

        setTimeout(() => (this.isStyleClip = false), 600);
      }
    }, 10);
  }

  /**
   * Gets the active {@link AutoComplete} {@link SearchMovieComponent}
   * @returns
   */
  getMovieSearchAutocomplete(): AutoComplete {
    return this.isDesktopViewport() ? this.autocomplete.last : this.autocomplete.first;
  }

  /**
   * @deprecated Don't found any use
   */
  getClasses(specialClass) {
    const classes = ['nav-link'];
    if (specialClass !== undefined) {
      classes.push(specialClass);
    }
    return classes;
  }

  /**
   * @deprecated Don't found any use
   */
  findElem(url: string): PageLink {
    return this.links.find(elem => elem.link.localeCompare(url) == 0);
  }

  listenForUserChanges() {
    this.authenticationService.$userStatus.pipe(takeUntil(this.destroy$)).subscribe(user => {
      if (user) {
        this.serializeLinks();
      }
    });
  }

  /**
   * Gets from environment if show the search in the small header.
   */
  get headerSMsearchTop() {
    return this.env.customPageParams?.headerSMsearchTop;
  }

  /**
   * Gets from enviroment if show the search in the menu of the small header.
   */
  get headerSMsearchCollapse() {
    return this.env.customPageParams?.headerSMsearchCollapse;
  }

  /**
   * Gets from environment if show the {@link LanguageSelectorComponent} in the header.
   */
  get hasTranslationHeader() {
    const hasTranslationHeader = this.env.customPageParams?.translationHeader;
    if (typeof hasTranslationHeader !== 'undefined') {
      return hasTranslationHeader;
    }
    return false;
  }

  get hasTranslationHeaderSM() {
    const hasTranslationHeader = this.env.customPageParams?.translationHeaderSm;
    if (typeof hasTranslationHeader !== 'undefined') {
      return hasTranslationHeader;
    }
    return false;
  }

  /**
   * Used when the user clicks the logo to removed the enabled classes.
   */
  onGoHome() {
    this.links.forEach(elem => {
      elem.isActive = false;
    });
  }

  /**
   * Changes the section in a branded page.
   * @param pageLink
   */
  gotoSection(pageLink: PageLink) {
    // set this link as active
    pageLink.isActive = true;
    // set the other links as inactive
    this.brandedPageLinks.forEach(link => {
      if (link.name != pageLink.name) link.isActive = false;
    });
    // hide menu
    this.toggleNavbar(false);
    // update themeservice menu link selected observable
    if (pageLink?.link) {
      this.router.navigateByUrl(pageLink.link);
    } else {
      this.brandedPageThemeService.setSectionSelected(pageLink.name);
    }
  }

  loadHeaderCustomTemplate() {
    const headerCustomLinkTemplate = this.env?.templateRefs?.headerCustomLinkTemplate;
    if (headerCustomLinkTemplate) {
      this.headerCustomLinkTemplate = this.templatesService.get(headerCustomLinkTemplate);
    }
  }

  /**
   * Gets from environment if the login is enabled.
   */
  get hasLogin() {
    if (this.isCommunityFeaturesEnabled) return true;
    return !!this.env.customPageParams?.login;
  }

  onHeaderLinkClicked(link: PageLink) {
    this.trackingService.trackEvent({
      action: 'HeaderLink',
      properties: {
        category: 'Header',
        label: link.name
      }
    });
    if (link.command && !link.command()) return;
    if (link.children) this.activateParentLink(link);
  }

  private activateParentLink(link: PageLink) {
    this.links.forEach(headerLink => {
      if (headerLink !== link && headerLink.children) headerLink.isActive = false;
    });
    link.isActive = true;
  }

  onHeaderLogoClicked() {
    this.trackingService.trackEvent({
      action: 'BrandLogo',
      properties: {
        category: 'Header',
        label: this.channel.name
      }
    });
  }

  getMyActivityLinkGroup() {
    const activityLinkGroup = this.myActivityService.getMyActivityLinkGroup(true);
    activityLinkGroup.children.forEach(link => {
      link.link = link.url;
    });
    return activityLinkGroup;
  }

  get isCommunityFeaturesEnabled() {
    return this.channelService.isRolePresent(UserRole.community);
  }
  get isLoginPopUpEnabled() {
    return this.env.customPageParams.login === 'popup';
  }

  getNormalizedUrl(link: PageLink): string {
    return this.normalizeRoute(!link?.isParent && !link?.isPopup && !link?.isExternal ? link.link : null);
  }

  normalizeRoute(route: string): string {
    return route?.replace(':' + Routes.USER_ID, this.authenticationService.getUserUsername());
  }

  serializeLinks() {
    this.links.forEach(link => {
      link.link = this.normalizeRoute(link.link);
      if (link.children) {
        link.children.forEach(childLink => {
          childLink.link = this.normalizeRoute(childLink.link);
        });
      }
    });
  }

  private setInitialHeaderCustomizationParams() {
    const config = this.envService.headerConfig();
    const hasHeaderFixed = this.hasHeaderFixed(config);

    if (!this.dynamicHostAttrsEnabled) {
      // Sets legacty fixed header only
      if (hasHeaderFixed) this.isFixed.set(hasHeaderFixed);
      return;
    }

    if (this.isFixed() === undefined) {
      if (hasHeaderFixed) this.isFixed.set(hasHeaderFixed);
      this.isFixed.set(this.envConfig<boolean>('positionFixed', config) || this.isFixed() || false);
    }

    if (this.scrolledStatus() !== 'top' && this.scrolledStatus() !== 'scrolled') {
      this.scrolledStatus.set('initial');
    }

    this.headerCustomizationClass.set(this.envConfig<HeaderCustomization['class']>('class', config) || '');
    this.logoFilter.set(this.envConfig<HeaderCustomization['logoFilter']>('logoFilter', config));
    this.overContentLg.set(
      this.envConfig<HeaderCustomization['positionOverContentLg']>('positionOverContentLg', config)
    );
    this.overContentSm.set(
      this.envConfig<HeaderCustomization['positionOverContentSm']>('positionOverContentSm', config)
    );

    // Same values but these ones won't be altered when scrolling
    this.isOverContentAtTopLg.set(
      this.envConfig<HeaderCustomization['positionOverContentLg']>('positionOverContentLg', config)
    );
    this.isOverContentAtTopSm.set(
      this.envConfig<HeaderCustomization['positionOverContentSm']>('positionOverContentSm', config)
    );

    this.syncHostRuntimeBindings();
  }

  private syncHostRuntimeBindings() {
    if (!this.isBrowser) return;

    const host = this.hostElement.nativeElement;
    if (this.displayStyle === undefined) {
      host.style.removeProperty('display');
    } else {
      host.style.display = this.displayStyle;
    }
  }

  /**
   * Uses a fixed scroll threshold instead of IntersectionObserver because the
   * fixed/absolute mobile header handoff made the observer boundary unstable.
   */
  private setupScrollTracking() {
    const config = this.envService.headerConfig();
    // Disables feature if not enabled in these conditions
    const onTopTracking = this.envConfig<boolean>('onTopTracking', config);
    if (!this.dynamicHostAttrsEnabled || !onTopTracking) return;
    const browserWindow = this.browserWindow;
    if (!browserWindow) return;

    this.isScrollTrackingEnabled = true;
    this.thresholdScrollHandler = () => this.syncTrackedHeaderStateFromScroll();
    browserWindow.addEventListener('scroll', this.thresholdScrollHandler, { passive: true });
  }

  private setupResizeTracking(): void {
    this.syncHeaderLayoutMode();
    const browserWindow = this.browserWindow;
    if (!browserWindow) return;

    this.resizeHandler = () => {
      this.syncHeaderLayoutMode();
      if (this.isScrollTrackingEnabled) {
        this.syncTrackedHeaderStateFromScroll();
      }
    };
    browserWindow.addEventListener('resize', this.resizeHandler, { passive: true });
  }

  private syncHeaderLayoutMode(): void {
    if (!this.isBrowser) {
      this.headerLayoutMode.set(this.initialHeaderLayoutMode ?? undefined);
      return;
    }
    this.headerLayoutMode.set(this.isDesktopViewport() ? 'desktop' : 'mobile');
  }

  private getInitialHeaderLayoutMode(): HeaderLayoutMode | undefined {
    if (!this.isBrowser) return this.initialHeaderLayoutMode ?? undefined;
    return this.isDesktopViewport() ? 'desktop' : 'mobile';
  }

  private syncTrackedHeaderStateFromScroll(force = false): void {
    if (!this.isScrollTrackingReady) return;

    this.updateTrackedHeaderState(this.isNearTopThreshold() ? 'top' : 'scrolled', force);
  }

  /**
   * Applies the scroll-driven header state and triggers the phased mobile
   * overlay return animation when going back from `scrolled` to `top`.
   */
  private updateTrackedHeaderState(nextStatus: 'top' | 'scrolled', force = false): void {
    const runtimeConfig = this.envService.headerConfig();
    const styleClass = this.envConfig<HeaderCustomization['class']>('class', runtimeConfig);
    const logFilter = this.envConfig<HeaderCustomization['logoFilter']>('logoFilter', runtimeConfig);
    const overContentLg = this.envConfig<HeaderCustomization['positionOverContentLg']>(
      'positionOverContentLg',
      runtimeConfig
    );
    const overContentSm = this.envConfig<HeaderCustomization['positionOverContentSm']>(
      'positionOverContentSm',
      runtimeConfig
    );
    const scrolledCustomization = this.envConfig<HeaderCustomization['scrolledCustomization']>(
      'scrolledCustomization',
      runtimeConfig
    );
    const hasDifferentScrolledClass = styleClass !== scrolledCustomization?.class;

    if (nextStatus === 'scrolled') {
      this.clearMobileOverlayTopTransition();
    }

    if (nextStatus === 'top' && this.mobileOverlayTopTransitionPhase() !== null) {
      return;
    }

    if (this.shouldUseMobileOverlayTopTransition(nextStatus, force, hasDifferentScrolledClass)) {
      this.runMobileOverlayTopTransition(
        hasDifferentScrolledClass,
        styleClass,
        logFilter,
        overContentLg,
        overContentSm,
        scrolledCustomization
      );
      return;
    }

    if (!force && this.scrolledStatus() === nextStatus) return;

    this.applyTrackedHeaderState(
      nextStatus,
      hasDifferentScrolledClass,
      styleClass,
      logFilter,
      overContentLg,
      overContentSm,
      scrolledCustomization
    );
  }

  private applyTrackedHeaderState(
    nextStatus: 'top' | 'scrolled',
    hasDifferentScrolledClass: boolean,
    styleClass: HeaderCustomization['class'],
    logFilter: HeaderCustomization['logoFilter'],
    overContentLg: HeaderCustomization['positionOverContentLg'],
    overContentSm: HeaderCustomization['positionOverContentSm'],
    scrolledCustomization: HeaderCustomization['scrolledCustomization']
  ): void {
    this.hasDifferentScrolledClass.set(hasDifferentScrolledClass);

    if (nextStatus === 'top') {
      this.scrolledStatus.set('top');
      this.scrollDirection.set('up');
    } else {
      this.scrolledStatus.set('scrolled');
      this.scrollDirection.set('down');
      this.hasScrolled.set(true);
    }

    this.syncHostRuntimeBindings();

    this.headerCustomizationClass.set(
      this.hasHeaderCustomizationProperty(styleClass, scrolledCustomization?.class) as string
    );

    this.logoFilter.set(
      this.hasHeaderCustomizationProperty(logFilter, scrolledCustomization?.logoFilter) as
        | HeaderCustomization['logoFilter']
        | undefined
    );

    this.overContentLg.set(
      this.hasHeaderCustomizationProperty(overContentLg, scrolledCustomization?.positionOverContentLg) as boolean
    );

    this.overContentSm.set(
      this.hasHeaderCustomizationProperty(overContentSm, scrolledCustomization?.positionOverContentSm) as boolean
    );

    this.syncHostRuntimeBindings();
    this.ref.detectChanges();
  }

  /**
   * Only overlay headers on mobile need the staged "old header out / new header in"
   * transition. Other header variants can switch state immediately.
   */
  private shouldUseMobileOverlayTopTransition(
    nextStatus: 'top' | 'scrolled',
    force: boolean,
    hasDifferentScrolledClass: boolean
  ): boolean {
    if (force || nextStatus !== 'top') return false;
    if (this.isDesktopViewport()) return false;
    if (!this.isFixed() || !this.hasScrolled()) return false;
    if (!this.isOverContentAtTopSm() || !hasDifferentScrolledClass) return false;
    if (this.scrolledStatus() !== 'scrolled') return false;
    if (this.mobileOverlayTopTransitionPhase() !== null) return false;

    return true;
  }

  /**
   * Keeps the current scrolled header styles during the first half of the return
   * animation, then swaps to the top styles while the header is off-screen.
   */
  private runMobileOverlayTopTransition(
    hasDifferentScrolledClass: boolean,
    styleClass: HeaderCustomization['class'],
    logFilter: HeaderCustomization['logoFilter'],
    overContentLg: HeaderCustomization['positionOverContentLg'],
    overContentSm: HeaderCustomization['positionOverContentSm'],
    scrolledCustomization: HeaderCustomization['scrolledCustomization']
  ): void {
    this.clearMobileOverlayTopTransition();
    this.hasDifferentScrolledClass.set(hasDifferentScrolledClass);
    this.scrollDirection.set('up');
    this.mobileOverlayTopTransitionPhase.set('out');
    this.syncHostRuntimeBindings();
    this.ref.detectChanges();

    const outTimer = setTimeout(() => {
      this.mobileOverlayTopTransitionPhase.set('in');
      this.applyTrackedHeaderState(
        'top',
        hasDifferentScrolledClass,
        styleClass,
        logFilter,
        overContentLg,
        overContentSm,
        scrolledCustomization
      );
    }, 150);

    const inTimer = setTimeout(() => {
      this.mobileOverlayTopTransitionPhase.set(null);
      this.syncHostRuntimeBindings();
      this.ref.detectChanges();
    }, 300);

    this.mobileOverlayTopTransitionTimers.push(outTimer, inTimer);
  }

  /**
   * Cancels any pending mobile overlay return animation and clears its phase flag.
   */
  private clearMobileOverlayTopTransition(): void {
    this.mobileOverlayTopTransitionTimers.forEach(timer => clearTimeout(timer));
    this.mobileOverlayTopTransitionTimers = [];
    if (this.mobileOverlayTopTransitionPhase() !== null) {
      this.mobileOverlayTopTransitionPhase.set(null);
    }
  }

  private isNearTopThreshold(): boolean {
    return this.getCurrentScrollTop() <= this.getTopThresholdPx() + 1;
  }

  private getCurrentScrollTop(): number {
    const browserWindow = this.browserWindow;
    if (!browserWindow) return 0;

    return (
      browserWindow.pageYOffset || this.documentRef.documentElement?.scrollTop || this.documentRef.body?.scrollTop || 0
    );
  }

  private getTopThresholdPx(): number {
    const browserWindow = this.browserWindow;
    if (!browserWindow) return 0;

    const thresholdEl = this.headerThreshold.nativeElement as HTMLElement;
    const computedTop = Number.parseFloat(browserWindow.getComputedStyle(thresholdEl).top || '');
    if (Number.isFinite(computedTop) && computedTop >= 0) return computedTop;

    const headerWrapper = this.hostElement.nativeElement.querySelector('.header-wrapper') as HTMLElement | null;
    return headerWrapper?.getBoundingClientRect().height || 0;
  }

  private hasHeaderCustomizationProperty(value: unknown | null, scrolledValue: unknown | null): unknown | null {
    const scrollStatus = this.scrolledStatus();
    if (scrollStatus === 'scrolled' && scrolledValue !== null && scrolledValue !== undefined) {
      return scrolledValue;
    }
    return value || false;
  }

  private isDesktopViewport(): boolean {
    const browserWindow = this.browserWindow;
    if (!browserWindow) return true;
    return browserWindow.innerWidth >= this.getDesktopMinWidthPx();
  }

  private getDesktopMinWidthPx(): number {
    if (!this.isBrowser) return DEFAULT_HEADER_DESKTOP_MIN_WIDTH;
    const hostStyles = this.browserWindow?.getComputedStyle(this.hostElement.nativeElement);
    const configuredValue = hostStyles?.getPropertyValue(HEADER_DESKTOP_MIN_WIDTH_CSS_VAR)?.trim();
    const minWidth = configuredValue ? Number.parseFloat(configuredValue) : Number.NaN;
    return Number.isFinite(minWidth) ? minWidth : DEFAULT_HEADER_DESKTOP_MIN_WIDTH;
  }

  /**
   * Help funtion for `scrollListenerEffect`
   */
  private updateAttributes(propName: string[], newValues: unknown[], currentValues: unknown[]): boolean {
    let hasChanges = false;
    for (let i = 0; i < propName.length; i++) {
      this[propName[i]] = newValues[i];
      if (this[propName[i]] !== currentValues[i]) {
        hasChanges = true;
      }
    }
    return hasChanges;
  }

  private envConfig<T>(prop: keyof HeaderCustomization, config?: HeaderCustomization): T {
    return (config ?? this.envService.headerConfig())?.[prop] as T;
  }

  ngOnDestroy() {
    const browserWindow = this.browserWindow;
    this.clearMobileOverlayTopTransition();
    if (browserWindow && this.thresholdScrollHandler) {
      browserWindow.removeEventListener('scroll', this.thresholdScrollHandler);
    }
    if (browserWindow && this.resizeHandler) {
      browserWindow.removeEventListener('resize', this.resizeHandler);
    }
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}

function resolveDynamicHostAttrsEnabled(config?: HeaderCustomization | null): boolean {
  return toBoolean(config?.dynamicHostAttrsEnabled);
}
